import { IObservable, Subject } from '~/rage/utils';
import { GameConfigService } from '../GameConfigService';
import { IInputService } from '../InputService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IVehicleService } from '../VehicleService';
import { ITuningTesterService, IVehicleMod } from './tuningTesterServiceTypes';

export class TuningTesterService implements ITuningTesterService {
	private readonly vehicleMods = new Subject<IVehicleMod[]>([]);
	private readonly sourceVehicleMods: IObservable<IVehicleMod[]>;

	constructor(
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
		private readonly input: IInputService,
		private readonly gameConfig: GameConfigService,
		private readonly vehicles: IVehicleService,
	) {
		this.sourceVehicleMods = this.vars.get('tuningTester:vehicleMods', []);

		const updateVehicleMods = this.updateVehicleMods.bind(this);
		this.sourceVehicleMods.subscribe(updateVehicleMods);
		this.gameConfig.getConfigs().subscribe(updateVehicleMods);
		this.vehicles.getLocalVehicleModel().subscribe(updateVehicleMods);
	}

	public getVehicleMods(): IObservable<IVehicleMod[]> {
		return this.vehicleMods;
	}

	public copyModId(modId: string): void {
		this.input.copyToClipboard(modId);
	}

	public async applyVehicleMod(
		modCategoryIndex: number,
		modIndex: number,
	): Promise<void> {
		await this.rpc.invokeServer('tuningTester:applyVehicleMod', {
			modCategoryIndex,
			modIndex,
		});
	}

	public async exitMenu(): Promise<void> {
		await this.rpc.invokeClient('tuningTester:exitMenu');
	}

	private updateVehicleMods(): void {
		this.vehicleMods.next(
			this.sourceVehicleMods.getValue().map((mod) => ({
				...mod,
				modIdsByIndex: this.getModIdsByIndex(mod.typeIndex),
			})),
		);
	}

	private getModIdsByIndex(typeIndex: number): Record<number, string> {
		const vehicleModel = this.vehicles.getLocalVehicleModel().getValue();
		const tuningMods = this.gameConfig.configs.vehicleTuning?.mods ?? [];
		const modIdsByIndex: Record<number, string> = {};

		for (const modConfig of tuningMods) {
			if (modConfig.modType !== typeIndex) {
				continue;
			}

			const isExactModel =
				vehicleModel !== null && modConfig.vehicleModel === vehicleModel;
			const isGlobalModel = modConfig.vehicleModel === null;

			if (!isExactModel && !isGlobalModel) {
				continue;
			}

			if (isExactModel || modIdsByIndex[modConfig.modIndex] === undefined) {
				modIdsByIndex[modConfig.modIndex] = modConfig.id;
			}
		}

		return modIdsByIndex;
	}
}
